import LZString from '/fp/compress.js';
import { isChromiumAndroid } from '/fp/platform.mjs';
import { bytesToBase64, base64ToBytes, deriveAesKey, ensureRsSessionId, peekRsSessionId } from '/utils.js';
import {
  getLocalStorageSafe,
  localStorageGetItemSafe,
  localStorageSetItemSafe,
  localStorageRemoveItemSafe,
} from '/fp/persistent-storage.js';
import {
  DEVICE_CACHE_KEY_PREFIX,
  deviceCacheKey,
  deviceCacheTag,
  deviceDataForStore,
  isCleanCollect,
  encodeDeviceRecord,
  decodeDeviceRecord,
  isDeviceRecordFresh,
} from '/fp/device-cache.mjs';

const KEY_SALT = 'rd';
const IV_BYTES = 12;

let _memory = null;
let _keyAppId = null;
let _keyPromise = null;

function removeExpiredRecords(keep, now) {
  const ls = getLocalStorageSafe();
  if (!ls) return;
  try {
    const stale = [];
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      if (!key || key === keep || key.indexOf(DEVICE_CACHE_KEY_PREFIX) !== 0) continue;
      const record = decodeDeviceRecord(ls.getItem(key));
      if (record && !isDeviceRecordFresh(record, record.tag, now)) stale.push(key);
    }
    for (let i = 0; i < stale.length; i++) ls.removeItem(stale[i]);
  } catch (e) {}
}

function recordKey(scope) {
  return deviceCacheKey({
    appId: scope.appId,
    fp: scope.products.fp,
    di: scope.products.di,
    fpOptions: scope.fpOptions,
  });
}

function currentTag(scope, sessionId) {
  return deviceCacheTag({
    appId: scope.appId,
    version: typeof __SDK_VERSION__ !== 'undefined' ? __SDK_VERSION__ : '',
    fp: scope.products.fp,
    di: scope.products.di,
    fpOptions: scope.fpOptions,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    sessionId: sessionId === undefined ? ensureRsSessionId() : sessionId,
  });
}

function storageKey(appId) {
  if (_keyPromise && _keyAppId === appId) return _keyPromise;
  _keyAppId = appId;
  _keyPromise = deriveStorageKey(appId).catch(() => null);
  return _keyPromise;
}

function deriveStorageKey(appId) {
  return deriveAesKey(appId, KEY_SALT, ['encrypt', 'decrypt']);
}

async function sealPayload(appId, json) {
  const key = await storageKey(appId);
  if (!key) return null;
  const bytes = LZString.compressToUint8Array(json);
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const cipher = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, key, bytes));
  const out = new Uint8Array(IV_BYTES + cipher.length);
  out.set(iv);
  out.set(cipher, IV_BYTES);
  return bytesToBase64(out);
}

async function openPayload(appId, record) {
  const bytes = base64ToBytes(record.payload);
  const key = await storageKey(appId);
  if (!key) return null;
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: bytes.subarray(0, IV_BYTES), tagLength: 128 },
    key,
    bytes.subarray(IV_BYTES)
  );
  return LZString.decompressFromUint8Array(new Uint8Array(plain));
}

function isPageRecord(record, tag) {
  return !!record && record.tag === tag;
}

export function hasFreshDeviceData(scope) {
  const sessionId = peekRsSessionId();
  if (!sessionId) return false;
  const tag = currentTag(scope, sessionId);
  if (isPageRecord(_memory, tag)) return true;
  return isDeviceRecordFresh(decodeDeviceRecord(localStorageGetItemSafe(recordKey(scope))), tag, Date.now());
}

export async function loadDeviceData(scope) {
  const now = Date.now();
  const tag = currentTag(scope);
  if (isPageRecord(_memory, tag)) return _memory;
  const key = recordKey(scope);
  const raw = localStorageGetItemSafe(key);
  if (raw === null) return null;
  const record = decodeDeviceRecord(raw);
  if (!isDeviceRecordFresh(record, tag, now)) {
    localStorageRemoveItemSafe(key);
    return null;
  }
  let data = null;
  try {
    const json = await openPayload(scope.appId, record);
    data = json ? JSON.parse(json) : null;
  } catch (e) {
    data = null;
  }
  if (!data || typeof data !== 'object') {
    localStorageRemoveItemSafe(key);
    return null;
  }
  if (record.timingSent) delete data.timing;
  _memory = { tag, savedAt: record.savedAt, data, stored: true, persist: true };
  return _memory;
}

function isSameRecord(a, b) {
  return !!a && !!b && a.savedAt === b.savedAt && a.tag === b.tag;
}

async function writeRecord(scope, record) {
  try {
    const payload = await sealPayload(scope.appId, JSON.stringify(record.data));
    if (!payload) return;
    const key = recordKey(scope);
    const timingSent = isSameRecord(_memory, record) && !!_memory.timingSent;
    localStorageSetItemSafe(key, encodeDeviceRecord({ savedAt: record.savedAt, tag: record.tag, payload, timingSent }));
    removeExpiredRecords(key, Date.now());
  } catch (e) {}
}

export async function saveDeviceData(scope, data) {
  const persist = isCleanCollect(data, { storageTimeoutOk: isChromiumAndroid() });
  _memory = { tag: currentTag(scope), savedAt: Date.now(), data: deviceDataForStore(data), stored: false, persist };
  if (persist) await writeRecord(scope, _memory);
  else localStorageRemoveItemSafe(recordKey(scope));
}

function markStoredTiming(scope, record, sent) {
  const key = recordKey(scope);
  const stored = decodeDeviceRecord(localStorageGetItemSafe(key));
  if (!isSameRecord(stored, record)) return;
  localStorageSetItemSafe(key, encodeDeviceRecord(Object.assign({}, stored, { timingSent: sent })));
}

export function takeStoredTiming(scope, record) {
  const current = _memory;
  if (!isSameRecord(current, record) || !current.data.timing) return null;
  const { timing, ...data } = current.data;
  const taken = Object.assign({}, current, { data, timingSent: true });
  _memory = taken;
  if (current.persist) markStoredTiming(scope, current, true);
  return function restoreStoredTiming() {
    if (_memory !== taken) return;
    _memory = current;
    if (current.persist) markStoredTiming(scope, current, false);
  };
}
